/**
 * Press releases shown at /press.
 *
 * Each entry mirrors a release in docs/press/. Dated releases appear only once
 * their date has arrived, using the same build-time cutoff as the blog: this
 * module runs during prerender AND again in the browser, so reading the wall
 * clock here would make the two disagree and break hydration.
 */

export interface PressRelease {
  slug: string;
  /** ISO date, YYYY-MM-DD. */
  date: string;
  headline: string;
  subhead: string;
  summary: string;
  /** Where a reader should go to see the thing the release describes. */
  href: string;
  hrefLabel: string;
}

const allReleases: PressRelease[] = [
  {
    slug: "40-free-tools",
    date: "2026-08-05",
    headline:
      "OmniCanvas Releases 40 Free Online Tools That Run Entirely In The Browser",
    subhead:
      "Handwriting-to-text, audio transcription, and background removal process locally on the user's device — no account, no upload, no server-side copy",
    summary:
      "21 new tools bring the free library to 40. The tools that use machine learning run inside the browser, on the visitor's own device, so no copy of the file reaches an OmniCanvas server. Every tool is free and none require an account or an email address.",
    href: "/tools",
    hrefLabel: "Browse the 40 tools",
  },
  {
    slug: "no-bot-meeting-notes",
    date: "2026-08-12",
    headline:
      "OmniCanvas Transcribes Meetings Without Sending a Bot Into the Call",
    subhead:
      "The AI note taker adds no participant to the meeting; on encrypted notes, transcription is forced to run on the user's own device",
    summary:
      "OmniCanvas records and transcribes meetings without adding a participant to the call. On an encrypted note, transcription runs on the user's own device and will not fall back to a cloud service.",
    href: "/meeting-notes",
    hrefLabel: "See how meeting notes work",
  },
  {
    slug: "free-tier-encryption",
    date: "2026-08-19",
    headline:
      "OmniCanvas Offers Zero-Knowledge Note Encryption on Its Free Tier",
    subhead:
      "Encryption runs in the browser using AES-256-GCM; the server stores only ciphertext, and OmniCanvas cannot read an encrypted note",
    summary:
      "Client-side encryption is included on the free tier, with no limit on notes or canvases. Keys are derived in the browser and the server stores only ciphertext.",
    href: "/faq",
    hrefLabel: "Read the encryption FAQ",
  },
  {
    slug: "guest-collaboration",
    date: "2026-08-26",
    headline:
      "OmniCanvas Lets People Collaborate on a Canvas in Real Time Without an Account",
    subhead:
      "Guests join from an invite link by entering a display name; encrypted notes cannot be shared at all, by design",
    summary:
      "A guest opens an invite link, enters a display name, and joins a live canvas — no signup. Encrypted notes are excluded from sharing entirely, because the server has no key with which to serve them.",
    href: "/demo",
    hrefLabel: "Try a live canvas",
  },
  {
    slug: "imports-exports",
    date: "2026-09-02",
    headline:
      "OmniCanvas Imports Notes From Apple Notes, Evernote, and JSON Canvas",
    subhead:
      "The importers preserve titles, timestamps, folder structure and tags; Evernote attachments currently arrive as placeholders",
    summary:
      "Existing libraries move across with their structure intact, and everything can be exported back out as Markdown, PDF, or a portable archive.",
    href: "/apple-notes-companion",
    hrefLabel: "See the Apple Notes path",
  },
];

// Frozen at build time (see vite.config.ts) for the same reason as the blog.
declare const __PUBLISH_CUTOFF__: string | undefined;
const publishCutoff = new Date(
  typeof __PUBLISH_CUTOFF__ === "string" ? __PUBLISH_CUTOFF__ : new Date().toISOString()
).getTime();

export const releases: PressRelease[] = allReleases
  .filter((release) => new Date(release.date + "T00:00:00Z").getTime() <= publishCutoff)
  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

/** Dates render identically during prerender and hydration: UTC, no locale. */
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

export function formatReleaseDate(date: string): string {
  const parsed = new Date(date + "T00:00:00Z");
  return `${MONTHS[parsed.getUTCMonth()]} ${parsed.getUTCDate()}, ${parsed.getUTCFullYear()}`;
}

/**
 * Where a release was syndicated.
 *
 * These are wire-service placements, not editorial coverage: the release was
 * distributed and each site republished it in its press-release section. The
 * page says so in as many words. Presenting them as "as seen in" — especially
 * with broadcaster logos — would claim newsroom coverage that did not happen,
 * and would be using those marks without permission.
 *
 * Every entry below was fetched and confirmed to return 200 with the release
 * body present. Nothing goes in this list unverified.
 */
export interface Syndication {
  outlet: string;
  where: string;
  url: string;
}

export const syndication: Record<string, Syndication[]> = {
  "no-bot-meeting-notes": [
    { outlet: "Local 3 News", where: "Chattanooga, TN", url: "https://www.local3news.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_239d9604-fd1c-59ea-ab95-80ee4f930157.html" },
    { outlet: "KVOA", where: "Tucson, AZ", url: "https://www.kvoa.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_f1f52e2c-128f-55fc-a560-dd1149bbecd6.html" },
    { outlet: "ABC12", where: "Flint, MI", url: "https://www.abc12.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_bc6a15d4-7df0-5c0d-9496-3cefede345b7.html" },
    { outlet: "WENY", where: "Elmira, NY", url: "https://www.weny.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_36bf7453-37c0-5ce6-8a70-4a69bdf8298f.html" },
    { outlet: "KTBS", where: "Shreveport, LA", url: "https://www.ktbs.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_c44bf70f-19a3-5efb-9433-fb8f8da2c1b4.html" },
    { outlet: "Montana Right Now", where: "Montana", url: "https://www.montanarightnow.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_eea09207-da55-56fc-8d6e-3fe9720c4f7b.html" },
    { outlet: "KWWL", where: "Waterloo, IA", url: "https://www.kwwl.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_551eb5c1-1196-55b2-8d64-be48d378473f.html" },
    { outlet: "Sea Coast Echo", where: "Bay St. Louis, MS", url: "https://www.seacoastecho.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_2afd5592-6004-50a6-a902-cccf34296ed2.html" },
    { outlet: "Wyoming Tribune Eagle", where: "Cheyenne, WY", url: "https://www.wyomingnews.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_9cede047-46ea-56fb-882e-32b770c905f3.html" },
    { outlet: "Business North", where: "Duluth, MN", url: "https://www.businessnorth.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_a5b228cc-4fe7-5329-a7bc-48c783e137a3.html" },
    { outlet: "Napa Valley Register", where: "Napa, CA", url: "https://napavalleyregister.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_ef16bbf1-eba3-572d-b7bd-ea154d3721a1.html" },
    { outlet: "Trinidad Express", where: "Trinidad & Tobago", url: "https://trinidadexpress.com/online_features/press_releases/omnicanvas-transcribes-meetings-without-sending-a-bot-into-the-call/article_47470f66-1eca-5177-a566-b1b9432e1c8d.html" },
    { outlet: "openPR", where: "Original release", url: "https://www.openpr.com/news/4602321" },
  ],
  "40-free-tools": [
    { outlet: "KAKE", where: "Wichita, KS", url: "https://www.kake.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_03e34c7e-a2de-59ff-9ea8-85a134879f58.html" },
    { outlet: "KVOA", where: "Tucson, AZ", url: "https://www.kvoa.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_f8af2d99-ae91-5f38-98be-c898f7a62a63.html" },
    { outlet: "ABC12", where: "Flint, MI", url: "https://www.abc12.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_9db472dc-bc35-57d7-9c9e-bc522caba577.html" },
    { outlet: "WBOC", where: "Salisbury, MD", url: "https://wboc.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_09abc89c-c3cd-52b3-95c9-a417d4728f2b.html" },
    { outlet: "KTEN", where: "Denison, TX", url: "https://www.kten.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_656dd04f-b06e-5329-a0f8-2fe8d3f75981.html" },
    { outlet: "Napa Valley Register", where: "Napa, CA", url: "https://napavalleyregister.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_46e2765c-027a-594c-bb5c-1a0514d55e86.html" },
    { outlet: "Denton Record-Chronicle", where: "Denton, TX", url: "https://dentonrc.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_100c83da-ef2c-5e56-8331-970104447997.html" },
    { outlet: "Arizona Daily Sun", where: "Flagstaff, AZ", url: "https://azdailysun.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_0ddbb1c5-157d-5546-872a-82c181f4a33a.html" },
    { outlet: "Santa Maria Times", where: "Santa Maria, CA", url: "https://santamariatimes.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_f05e6262-ea49-5915-adcf-a907d6e44fe6.html" },
    { outlet: "Business North", where: "Duluth, MN", url: "https://www.businessnorth.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_537137c6-b069-5aa6-b98b-afabd81f40d0.html" },
    { outlet: "Trinidad Express", where: "Trinidad & Tobago", url: "https://trinidadexpress.com/online_features/press_releases/omnicanvas-releases-40-free-online-tools-that-run-entirely-in-the-browser-without-uploading-files/article_7e2f580c-e1a9-5e95-891d-42e6338f8bee.html" },
    { outlet: "openPR", where: "Original release", url: "https://www.openpr.com/news/4597583" },
  ],
  "free-tier-encryption": [
    { outlet: "openPR", where: "Original release", url: "https://www.openpr.com/news/4607900" },
  ],
  "guest-collaboration": [
    { outlet: "openPR", where: "Original release", url: "https://www.openpr.com/news/4614228" },
  ],
};

/**
 * Distinct domains that republished each release, from the wire distribution
 * report (docs/press/distribution/<slug>.json, distinctRegistrableDomains).
 *
 * Deliberately NOT the report's raw row count, which is the bigger number the
 * wire service leads with: it counts the same release many times over on one
 * domain, and it counts a stock-ticker widget on farm co-op sites as a
 * placement. Domains is the smallest defensible number, so it is the one we
 * publish. Regenerate with scripts/parse-press-report.mjs.
 */
export const syndicationTotals: Record<string, number> = {
  "40-free-tools": 762,
  "no-bot-meeting-notes": 982,
  "free-tier-encryption": 282,
  "guest-collaboration": 261,
};
